import { useState, useEffect, useRef, useId } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAuth } from '@/hooks/useAuth';
import { LemonadeAvatar } from './LemonadeAvatar';
import { AuthEmailSentPanel, AUTH_EMAIL_SECONDARY_ACTION_CLASS } from './AuthEmailSentPanel';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Spinner } from '@/components/ui/spinner';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { signupEmailSchema, otpSchema, phoneLoginSchema } from '@/lib/schemas/auth';
import {
  stripPhoneDigits,
  formatNanpPhone,
  toE164NorthAmerica,
} from '@/lib/phone';
import { loginRateLimitMessage } from '@/lib/auth-email-copy';
import { completeEmailSignUpTransfer } from '@/lib/clerk-auth-flows';
import { consumeSsoTermsAcceptance } from '@/lib/pivot-signup';
import { analytics } from '@/lib/analytics';
import { SocialAuthButtons } from './SocialAuthButtons';

/**
 * REK-1930: when the edge function reports a rate limit without a precise
 * remaining window (e.g. it has not been redeployed yet with `retryAfter`),
 * fall back to disabling Send Link for this many seconds so it still
 * re-enables — without fabricating a precise countdown in the copy.
 */
const RATE_LIMIT_FALLBACK_SECONDS = 60;

interface LemonadeLoginProps {
  initialEmail?: string;
  // REK-1903: when the login email was ALREADY sent upstream (e.g. the signup
  // funnel detected an existing account and sent the login OTP via sign-in-otp),
  // go straight to the "Check your email" stage WITHOUT re-sending — otherwise
  // the auto-send below would mint a second code and invalidate the first.
  alreadySent?: boolean;
  // REK-1924: where to send the user after a successful login. Defaults to
  // /dashboard; a survey deep link sets it to the in-app surveys path with the survey id.
  nextPath?: string;
  onSwitchToSignup: (email?: string) => void;
  // REK-2008 / REK-2167: while completeEmailSignUpTransfer activates the
  // session, suspend Auth.tsx's authenticated→dashboard redirect so this
  // component's /onboarding navigate wins.
  onPivotStateChange?: (active: boolean) => void;
}

type EmailFormValues = z.infer<typeof signupEmailSchema>;
type PhoneFormValues = z.infer<typeof phoneLoginSchema>;
type OtpFormValues = z.infer<typeof otpSchema>;

// REK-1986 (SSO.5): the NEW phone tab. Clerk's SMS allowlist is US + CA only
// (SSO.0 toll-fraud guard); both share the +1 country code.
type LoginMethod = 'email' | 'phone';
type PhoneCountry = 'US' | 'CA';

export function LemonadeLogin({
  initialEmail,
  alreadySent,
  nextPath = '/dashboard',
  onSwitchToSignup,
  onPivotStateChange,
}: LemonadeLoginProps) {
  const navigate = useNavigate();
  const {
    sendMagicLink,
    sendPhoneOtp,
    verifyOtp,
    verifyPhoneOtp,
    setAccountLockedError,
    isAuthenticated,
  } = useAuth();
  // REK-2008 / REK-2167: 'new-user-confirm' is the in-box ToS acceptance screen
  // shown when the email-code OTP verifies as 'transferable' (new address,
  // signUpIfMissing path). 'creating' is the post-confirm account provisioning wait.
  const [stage, setStage] = useState<'email' | 'sent' | 'new-user-confirm' | 'creating'>('email');
  // REK-1986: phone login exists only under Clerk custom flows — the tabs
  // never render for the legacy Supabase engine, so `method` stays 'email'.
  const [method, setMethod] = useState<LoginMethod>('email');
  const [phoneCountry, setPhoneCountry] = useState<PhoneCountry>('US');
  const [isLoading, setIsLoading] = useState(false);
  const autoSentRef = useRef(false);
  // Mirrors "the parent was told the transfer/signup pivot is active" so the
  // unmount cleanup can release Auth's suspended redirect (Bugbot: stranding
  // `isCompletingSignup` if LemonadeLogin unmounts mid-`creating`).
  const pivotNotifiedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  // REK-1930: rate-limit cooldown for "too many login links" (3 / 15 min per IP,
  // enforced in multi-email-auth; Clerk applies its own 429s under SSO.5).
  // `rateLimitCooldown` ticks down to drive both the disabled Send Link /
  // Resend button and the live countdown copy.
  const [rateLimitCooldown, setRateLimitCooldown] = useState(0);
  // Whether `rateLimitCooldown` reflects the real backend window (show a precise
  // countdown) vs an unknown-window fallback (show generic "try again" copy).
  const [rateLimitKnown, setRateLimitKnown] = useState(false);
  // REK-1930: one-shot screen-reader announcement, set once when the limit is
  // hit (includes the retry timing) and NOT updated per tick, so SR users learn
  // the wait without being re-announced every second. Lives in an always-present
  // live region so the announcement fires reliably across NVDA/VoiceOver.
  const [rateLimitAnnouncement, setRateLimitAnnouncement] = useState('');
  // D-2 (REK-1986): Clerk emails carry a code ONLY (no magic link), so the
  // code input is always shown immediately on the sent stage.
  const [showOtpInput, setShowOtpInput] = useState<boolean>(true);
  const [verifyEmail, setVerifyEmail] = useState('');
  // Display-formatted phone the SMS code went to (e.g. "+1 (555) 555-0123").
  const [verifyPhone, setVerifyPhone] = useState('');
  // REK-2167: email address confirmed transferable — shown read-only on the
  // new-user-confirm stage and passed to completeEmailSignUpTransfer.
  const [newUserEmail, setNewUserEmail] = useState('');
  // Combined 18+/ToS attestation checkbox on the new-user-confirm stage.
  const [newUserTermsAccepted, setNewUserTermsAccepted] = useState(false);
  // ISO stamped when the checkbox flips on — never invent at submit time.
  const [newUserTermsAcceptedAtIso, setNewUserTermsAcceptedAtIso] = useState<string | null>(null);
  const newUserTosId = useId();

  const emailForm = useForm<EmailFormValues>({
    resolver: zodResolver(signupEmailSchema),
    defaultValues: { email: initialEmail || '' },
    mode: 'onSubmit',
  });

  const phoneForm = useForm<PhoneFormValues>({
    resolver: zodResolver(phoneLoginSchema),
    defaultValues: { phone: '' },
    mode: 'onSubmit',
  });

  const otpForm = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: { code: '' },
    mode: 'onSubmit',
  });

  // Countdown timer for resend
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // REK-1930: countdown for the rate-limit cooldown. When it reaches 0 the
  // Send Link / Resend buttons re-enable and the rate-limit copy + SR
  // announcement clear.
  useEffect(() => {
    if (rateLimitCooldown > 0) {
      const timer = setTimeout(() => setRateLimitCooldown((c) => c - 1), 1000);
      return () => clearTimeout(timer);
    }
    setRateLimitAnnouncement('');
  }, [rateLimitCooldown]);

  // Auto-send magic link when redirected from onboarding with a pre-filled
  // email. When `alreadySent` is set the OTP was already sent upstream, so we
  // jump straight to the "Check your email" stage without sending again
  // (avoids a duplicate code that would invalidate the first). REK-1903.
  useEffect(() => {
    if (!initialEmail || autoSentRef.current) return;
    autoSentRef.current = true;
    if (alreadySent) {
      setVerifyEmail(initialEmail);
      setStage('sent');
      setResendCooldown(60);
    } else {
      void emailForm.handleSubmit(handleSendLink)();
    }
    // handleSendLink/emailForm intentionally omitted: this runs once on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialEmail, alreadySent]);

  // Auth state from AuthProvider already updates when the user clicks the
  // magic link in another tab — no need for a local supabase.auth subscription.
  // When isAuthenticated flips true on the "sent" stage, route to dashboard.
  // (REK-2167: the pivot guard is removed — new users land on 'new-user-confirm'
  // before their session activates, so stage === 'sent' + isAuthenticated always
  // means a normal sign-in completion.)
  useEffect(() => {
    if (stage === 'sent' && isAuthenticated) {
      navigate(nextPath);
    }
  }, [stage, isAuthenticated, navigate, nextPath]);

  const handleRateLimit = (retryAfter: number | null | undefined) => {
    const known = typeof retryAfter === 'number' && retryAfter > 0;
    setRateLimitKnown(known);
    setRateLimitCooldown(known ? retryAfter : RATE_LIMIT_FALLBACK_SECONDS);
    // Announce once, with the retry timing, so SR users learn the wait
    // (the visible countdown ticks but is intentionally not a live region).
    setRateLimitAnnouncement(loginRateLimitMessage(known ? retryAfter : null));
    setError(null);
  };

  /**
   * Send the login OTP.
   *
   * REK-2167: `startEmailCodeSignIn` uses `signUpIfMissing: true`, so the
   * `userExists === false` probe-and-pivot branch is no longer needed here —
   * new addresses receive the same OTP flow and land in `handleVerifyOtp` as
   * `'transferable'` after code entry.
   */
  const sendLoginCode = async (values: EmailFormValues) => {
    if (isLoading || rateLimitCooldown > 0) return;
    setError(null);
    setIsLoading(true);

    try {
      const { data, error, rateLimited, retryAfter, accountLocked } = await sendMagicLink(values.email, nextPath);

      // REK-1930: "Too many login links requested" — disable Send Link / Resend
      // and tick down the remaining cooldown (precise when the backend reports
      // it, otherwise a generic fallback). Stay on the current stage.
      if (rateLimited) {
        handleRateLimit(retryAfter);
        return;
      }

      // REK-1754: intentional send-time lock UX (residual tell after REK-1732).
      if (accountLocked) {
        setAccountLockedError(error?.message || 'Your account has been locked. Please contact support@reklaimyou.com for assistance.');
        return;
      }

      if (error) {
        if (error.message.includes('Signups not allowed') || error.message.includes('User not found')) {
          throw new Error('No account found with this email. Please sign up first.');
        }
        throw error;
      }

      // Unused under the Clerk engine but kept for the legacy Supabase path.
      void data;

      // Always confirm against the submitted address — never a primary from the API.
      setVerifyEmail(values.email);
      setStage('sent');
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send link');
    } finally {
      setIsLoading(false);
    }
  };

  /** Form-facing submit handler (react-hook-form passes (values, event)). */
  const handleSendLink = (values: EmailFormValues) => sendLoginCode(values);

  // ── REK-2167: new-user confirm (completeEmailSignUpTransfer) ─────────────

  const notifyPivotState = (active: boolean) => {
    pivotNotifiedRef.current = active;
    onPivotStateChange?.(active);
  };

  // If the card unmounts mid-transfer (e.g. Auth "Start over" during
  // `creating`), release the parent's suspended authenticated-redirect —
  // otherwise a later login on the same /auth mount strands the user.
  useEffect(() => {
    return () => {
      if (pivotNotifiedRef.current) onPivotStateChange?.(false);
    };
    // Unmount-only cleanup; onPivotStateChange is stable for the page's life.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleNewUserConfirm = async () => {
    if (isLoading || !newUserTermsAccepted || !newUserTermsAcceptedAtIso) return;
    setError(null);
    setIsLoading(true);
    // Suspend Auth.tsx auto-redirect before setActive activates the session.
    notifyPivotState(true);
    setStage('creating');
    try {
      const result = await completeEmailSignUpTransfer({
        tosAccepted: true,
        termsAcceptedAtIso: newUserTermsAcceptedAtIso,
      });
      if (result.status === 'error') {
        notifyPivotState(false);
        if (result.error.accountLocked) {
          setAccountLockedError(result.error.message);
          setStage('sent');
          return;
        }
        setError(result.error.message);
        setStage('new-user-confirm');
        return;
      }
      navigate(
        nextPath && nextPath !== '/dashboard'
          ? `/onboarding?next=${encodeURIComponent(nextPath)}`
          : '/onboarding',
      );
    } catch (err) {
      notifyPivotState(false);
      setError(err instanceof Error ? err.message : 'Account setup failed');
      setStage('new-user-confirm');
    } finally {
      setIsLoading(false);
    }
  };


  // REK-1986 (SSO.5): NEW phone login — send the SMS OTP via Clerk's
  // phone_code custom flow. Unknown numbers surface an inline error (there
  // is no phone signup on this surface — capture-at-signup is flag-gated OFF).
  const handleSendPhoneCode = async (values: PhoneFormValues) => {
    if (isLoading || rateLimitCooldown > 0) return;
    setError(null);
    setIsLoading(true);

    try {
      const digits = stripPhoneDigits(values.phone);
      const { error, rateLimited, retryAfter } = await sendPhoneOtp(toE164NorthAmerica(digits));

      if (rateLimited) {
        handleRateLimit(retryAfter);
        return;
      }

      if (error) throw error;

      setVerifyPhone(`+1 ${formatNanpPhone(digits)}`);
      setStage('sent');
      setResendCooldown(60);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendCooldown > 0 || rateLimitCooldown > 0 || isLoading) return;
    if (method === 'phone') {
      const values = phoneForm.getValues();
      if (!values.phone) return;
      await handleSendPhoneCode(values);
      return;
    }
    const values = emailForm.getValues();
    if (!values.email) return;
    await handleSendLink(values);
  };

  const handleVerifyOtp = async (values: OtpFormValues) => {
    if (isLoading) return;
    setError(null);
    setIsLoading(true);

    try {
      const targetEmail = verifyEmail || emailForm.getValues('email');
      const { data, error, accountLocked } =
        method === 'phone'
          ? await verifyPhoneOtp(values.code)
          : await verifyOtp(targetEmail, values.code);

      // REK-1732: legacy sign-in-otp no longer returns userExists — this path
      // never pivots on existence disclosure.
      // REK-2167: new-user path — the OTP verifies but Clerk reports the
      // sign-in as 'transferable' (signUpIfMissing hit a new address). Ask
      // the user to accept ToS before completing the sign-up transfer.
      // Prefer the submitted address for display; Clerk's safeIdentifier may be masked.
      if (data && 'status' in data && data.status === 'transferable') {
        const safeIdentifier =
          (data as { status: 'transferable'; safeIdentifier?: string }).safeIdentifier ?? '';
        // Restore funnel start for login-origin email signups (CHOICE enterPivot
        // used to emit this; transferable is the REK-2167 equivalent gate).
        analytics.trackEvent({
          name: 'signup_started',
          properties: { source_page: 'auth_login', cta_location: 'email_otp_transfer' },
        });
        setNewUserEmail(targetEmail || safeIdentifier);
        setStage('new-user-confirm');
        return;
      }

      if (error) {
        if (accountLocked) {
          setAccountLockedError(error.message);
          return;
        }
        throw error;
      }

      navigate(nextPath);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Invalid code. Please try again.';
      // Both engines return similar errors for wrong / expired / used codes.
      // When it looks like an expiry, point the user at the Resend action
      // (which mints a fresh code — plus a link on the legacy Supabase email)
      // rather than leaving them to retype a dead code. The message renders in
      // the AuthEmailSentPanel error slot, directly under the OTP input, where
      // the resend action also lives. Copy adapts to the channel (D-2: the
      // Clerk email is code-only, and the phone tab resends a text).
      const expired = /expired|invalid|not found|token/i.test(message);
      const resendAction = method === 'phone' ? 'Resend code' : 'Resend email';
      const freshNoun = 'fresh code';
      setError(
        expired
          ? `That code has expired or is invalid. Use “${resendAction}” above to get a ${freshNoun}, or re-check the code and try again.`
          : message,
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleUseDifferentIdentifier = () => {
    setStage('email');
    setError(null);
    setShowOtpInput(true);
    otpForm.reset({ code: '' });
  };

  const sentEmail = verifyEmail || emailForm.getValues('email');
  const sentIdentifier = method === 'phone' ? verifyPhone : sentEmail;

  // REK-1930: live rate-limit copy (with the ticking countdown when the precise
  // window is known). Takes precedence over the generic error slot.
  const rateLimitMessage =
    rateLimitCooldown > 0 ? loginRateLimitMessage(rateLimitKnown ? rateLimitCooldown : null) : null;

  const sendButtonLabel = 'Send Code';

  return (
    <div className="flex-1 flex flex-col items-center justify-start pt-16 px-6">
      <LemonadeAvatar />

      {/* REK-1930: announce the rate limit ONCE to screen readers. This live
          region is ALWAYS mounted (empty when idle) and only its text content
          toggles, which is the reliable pattern for `role="status"`
          announcements across NVDA/VoiceOver. The visible countdown ticks every
          second but is intentionally NOT a live region, so SR users hear the
          message (with the retry timing) once rather than on every tick. */}
      <span className="sr-only" role="status">
        {rateLimitAnnouncement}
      </span>

      <div className="w-full max-w-lg mt-8 text-center space-y-8">
        <div className="space-y-2">
          <h1 className="text-4xl lg:text-5xl font-light text-foreground">
            {stage === 'email'
              ? 'Welcome back!'
              : stage === 'new-user-confirm'
                ? 'Almost there!'
                : stage === 'creating'
                  ? 'Setting up your account…'
                  : method === 'phone'
                    ? 'Check your phone'
                    : 'Check your email'}
          </h1>
          {stage === 'email' && (
            <p className="text-lg text-muted-foreground">
              {method === 'phone' ? 'Enter your phone number to sign in' : 'Enter your email to sign in'}
            </p>
          )}
          {stage === 'new-user-confirm' && (
            <p className="text-lg text-muted-foreground">
              One last step — confirm you’re 18+ and agree to our terms.
            </p>
          )}
        </div>

        {/* REK-1986: Email | Phone method tabs — Clerk custom flows. */}
        {stage === 'email' && (
          <Tabs
            value={method}
            onValueChange={(value) => {
              setMethod(value as LoginMethod);
              setError(null);
            }}
          >
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="email">Email</TabsTrigger>
              <TabsTrigger value="phone">Phone</TabsTrigger>
            </TabsList>
          </Tabs>
        )}

        {stage === 'new-user-confirm' ? (
          // REK-2167: ToS acceptance before signUp.create({ transfer: true }).
          <div className="space-y-4 text-left">
            <div>
              <Input
                type="email"
                aria-label="Email address"
                value={newUserEmail}
                readOnly
                disabled
                className="h-14 text-lg px-4 border-2 bg-muted cursor-not-allowed"
              />
            </div>

            {error && (
              <p className="text-base text-destructive" role="alert">
                {error}
              </p>
            )}

            {/* Combined 18+/ToS attestation. Markup mirrors the OTP-step original:
                the <Label> is the checkbox’s only accessible-name source, with
                the linked anchors OUTSIDE the label so a link click can never
                toggle the checkbox. */}
            <div className="flex items-start gap-2">
              <Checkbox
                id={newUserTosId}
                checked={newUserTermsAccepted}
                onCheckedChange={(checked) => {
                  const accepted = checked === true;
                  setNewUserTermsAccepted(accepted);
                  setNewUserTermsAcceptedAtIso(accepted ? new Date().toISOString() : null);
                }}
                disabled={isLoading}
                className="mt-0.5"
              />
              <p className="text-sm font-normal leading-snug text-muted-foreground">
                <Label
                  htmlFor={newUserTosId}
                  className="inline text-sm font-normal leading-snug text-muted-foreground"
                >
                  I confirm that I am 18 years of age or older, and I agree to the
                  <span className="sr-only"> Terms and Conditions and Privacy Policy.</span>
                </Label>{' '}
                <a
                  href="/terms"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Terms and Conditions
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>{' '}
                and{' '}
                <a
                  href="/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline"
                >
                  Privacy Policy
                  <span className="sr-only"> (opens in a new tab)</span>
                </a>
                .
              </p>
            </div>

            <Button
              type="button"
              onClick={() => void handleNewUserConfirm()}
              disabled={!newUserTermsAccepted || isLoading}
              size="lg"
              className="w-full h-14 text-lg"
            >
              {isLoading ? (
                <>
                  <Spinner className="size-5 mr-2" />
                  Creating account…
                </>
              ) : (
                'Create my account'
              )}
            </Button>
          </div>
        ) : stage === 'creating' ? (
          <div className="space-y-4 py-8 flex flex-col items-center">
            <Spinner className="size-10 text-primary" />
            <p className="text-lg text-muted-foreground">
              One moment while we finish creating your account.
            </p>
          </div>
        ) : stage === 'email' ? (
          <div className="space-y-4">
          {method === 'phone' ? (
            // Distinct keys on the two <Form> subtrees: React must UNMOUNT
            // one and MOUNT the other on tab switch — react-hook-form's
            // Controller does not support swapping the `control` prop on a
            // reconciled-in-place element (the field would stay registered
            // against the previous form and silently drop every onChange).
            <Form {...phoneForm} key="phone-login">
              <form onSubmit={phoneForm.handleSubmit(handleSendPhoneCode)} className="space-y-4">
                <FormField
                  control={phoneForm.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="sr-only">Phone number</FormLabel>
                      <div className="flex gap-2">
                        <Select
                          value={phoneCountry}
                          onValueChange={(value) => setPhoneCountry(value as PhoneCountry)}
                          disabled={isLoading}
                        >
                          <SelectTrigger
                            aria-label="Country"
                            className="h-14 w-28 shrink-0 border-2 text-lg"
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="US">US +1</SelectItem>
                            <SelectItem value="CA">CA +1</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormControl>
                          <Input
                            {...field}
                            type="tel"
                            inputMode="tel"
                            autoComplete="tel-national"
                            placeholder="(555) 555-0123"
                            disabled={isLoading}
                            autoFocus
                            onChange={(e) =>
                              field.onChange(formatNanpPhone(stripPhoneDigits(e.target.value)))
                            }
                            className="h-14 flex-1 text-lg px-4 border-2"
                          />
                        </FormControl>
                      </div>
                      <FormMessage className="text-left" />
                    </FormItem>
                  )}
                />

                {rateLimitMessage ? (
                  <p className="text-base text-destructive text-left">{rateLimitMessage}</p>
                ) : (
                  error &&
                  (error.includes('support@reklaimyou.com') ? (
                    <p className="text-base text-destructive text-center">
                      Your account has been locked. Please contact{' '}
                      <a href="mailto:support@reklaimyou.com" className="underline font-medium">
                        support@reklaimyou.com
                      </a>{' '}
                      for assistance.
                    </p>
                  ) : (
                    <p className="text-base text-destructive text-left">{error}</p>
                  ))
                )}

                <Button
                  type="submit"
                  disabled={isLoading || rateLimitCooldown > 0}
                  size="lg"
                  className="w-full h-14 text-lg"
                >
                  {isLoading ? (
                    <>
                      <Spinner className="size-5 mr-2" />
                      Sending...
                    </>
                  ) : (
                    'Send Code'
                  )}
                </Button>
              </form>
            </Form>
          ) : (
            <Form {...emailForm} key="email-login">
              <form onSubmit={emailForm.handleSubmit(handleSendLink)} className="space-y-4">
                <FormField
                  control={emailForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="sr-only">Email address</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="email"
                          placeholder="Email address"
                          disabled={isLoading}
                          autoFocus
                          className="h-14 text-lg px-4 border-2"
                        />
                      </FormControl>
                      <FormMessage className="text-left" />
                    </FormItem>
                  )}
                />

                {rateLimitMessage ? (
                  <p className="text-base text-destructive text-left">
                    {rateLimitMessage}
                  </p>
                ) : (
                  error && (
                    error.includes('support@reklaimyou.com') ? (
                      // REK-1754: the account-locked notice is a page-level status,
                      // not a field error — center it to match the card layout.
                      <p className="text-base text-destructive text-center">
                        Your account has been locked. Please contact{' '}
                        <a href="mailto:support@reklaimyou.com" className="underline font-medium">
                          support@reklaimyou.com
                        </a>{' '}
                        for assistance.
                      </p>
                    ) : (
                      <p className="text-base text-destructive text-left">{error}</p>
                    )
                  )
                )}

                <Button
                  type="submit"
                  disabled={isLoading || rateLimitCooldown > 0}
                  size="lg"
                  className="w-full h-14 text-lg"
                >
                  {isLoading ? (
                    <>
                      <Spinner className="size-5 mr-2" />
                      Sending...
                    </>
                  ) : (
                    sendButtonLabel
                  )}
                </Button>
              </form>
            </Form>
          )}

          <SocialAuthButtons
              leadingDivider
              redirectUrl={
                nextPath && nextPath !== '/dashboard'
                  ? `/auth/sso-callback?next=${encodeURIComponent(nextPath)}`
                  : '/auth/sso-callback'
              }
              redirectUrlComplete={nextPath || '/dashboard'}
              disabled={isLoading || rateLimitCooldown > 0}
              onBeforeRedirect={() => {
                // Purge any stale terms-acceptance flag so this login-path
                // OAuth cannot carry a prior pivot-signup affirmation into
                // AuthSsoCallback and trigger a signup without fresh consent.
                consumeSsoTermsAcceptance();
              }}
              onError={(err) => {
                if (err.rateLimited) {
                  handleRateLimit(err.retryAfter);
                  return;
                }
                setError(err.message);
              }}
              />
          </div>
        ) : (
          <AuthEmailSentPanel
            email={sentIdentifier}
            intent={'sign-in'}
            channel={method === 'phone' ? 'sms' : 'email'}
            resendCooldown={resendCooldown}
            isLoading={isLoading}
            onResend={handleResend}
            disableResend={rateLimitCooldown > 0}
            error={
              rateLimitMessage
                ? rateLimitMessage
                : error?.includes('support@reklaimyou.com')
                  ? null
                  : error
            }
            onEnterCodeInstead={!showOtpInput ? () => setShowOtpInput(true) : undefined}
            trailingAction={
              <Button
                type="button"
                variant="ghost"
                onClick={handleUseDifferentIdentifier}
                disabled={isLoading}
                className={AUTH_EMAIL_SECONDARY_ACTION_CLASS}
              >
                {method === 'phone' ? 'Use a different number' : 'Use a different email'}
              </Button>
            }
          >
            {showOtpInput ? (
              <Form {...otpForm}>
                <form onSubmit={otpForm.handleSubmit(handleVerifyOtp)} className="space-y-4 text-left">
                  {/* REK-2167: ToS moved to post-OTP `new-user-confirm` (transferable
                      path). Existing-user OTP verify has no ToS gate here. */}
                  <FormField
                    control={otpForm.control}
                    name="code"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="sr-only">6-digit code</FormLabel>
                        <p className="text-sm text-muted-foreground text-center">
                          {method === 'phone'
                            ? 'Enter the 6-digit code from your text message.'
                            : 'Enter the 6-digit code from your email.'}
                        </p>
                        <FormControl>
                          <InputOTP
                            maxLength={6}
                            autoComplete="one-time-code"
                            inputMode="numeric"
                            value={field.value}
                            onChange={(value) => field.onChange(value.replace(/\D/g, '').slice(0, 6))}
                            onComplete={(code) => {
                              const cleaned = code.replace(/\D/g, '').slice(0, 6);
                              // REK-2167: the pivot ToS gate is removed — new users are
                              // handled via the 'new-user-confirm' stage, not an OTP gate.
                              if (cleaned.length === 6 && !isLoading) {
                                void handleVerifyOtp({ code: cleaned });
                              }
                            }}
                            disabled={isLoading}
                            autoFocus
                            containerClassName="justify-center"
                          >
                            <InputOTPGroup>
                              <InputOTPSlot index={0} />
                              <InputOTPSlot index={1} />
                              <InputOTPSlot index={2} />
                              <InputOTPSlot index={3} />
                              <InputOTPSlot index={4} />
                              <InputOTPSlot index={5} />
                            </InputOTPGroup>
                          </InputOTP>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <Button
                    type="submit"
                    disabled={isLoading}
                    size="lg"
                    className="w-full h-14 text-lg"
                  >
                    {isLoading ? (
                      <>
                        <Spinner className="size-5 mr-2" />
                        Verifying...
                      </>
                    ) : (
                      'Sign In'
                    )}
                  </Button>
                </form>
              </Form>
            ) : null}
          </AuthEmailSentPanel>
        )}

        {stage === 'sent' && error?.includes('support@reklaimyou.com') && (
          <p className="text-base text-destructive">
            Your account has been locked. Please contact{' '}
            <a href="mailto:support@reklaimyou.com" className="underline font-medium">
              support@reklaimyou.com
            </a>{' '}
            for assistance.
          </p>
        )}

        {stage === 'email' && (
          <p className="text-lg text-muted-foreground">
            Don&apos;t have an account?{' '}
            <Button
              type="button"
              variant="link"
              onClick={() => onSwitchToSignup(emailForm.getValues('email')?.trim() || undefined)}
              className="text-primary hover:underline font-medium px-0"
            >
              Sign up
            </Button>
          </p>
        )}

        {/* REK-2167: Clerk invisible CAPTCHA mount — always present (not gated on
            stage === 'email'). Resend from OTP and Create-my-account transfer both
            call signIn/signUp.create after leaving the email stage; AuthSsoCallback
            keeps the same always-mounted pattern for OAuth transfer. */}
        <div id="clerk-captcha" />
      </div>
    </div>
  );
}
